import type { OAuthProvider } from './postMessage';

export type InvoiceManagerPendingAction =
  | {
      type: 'SEND_INVOICE';
      invoiceId: string;
      quoteId: string;
      args: {
        sendViaOutlook: boolean;
        saveToOneDrive: boolean;
        scheduleReminder: boolean;
      };
    }
  | {
      type: 'SEND_OUTLOOK_EMAIL' | 'UPLOAD_ONEDRIVE' | 'CHECK_M365_STATUS';
      invoiceId: string;
      quoteId: string;
      args: {
        sendViaOutlook: boolean;
        saveToOneDrive: boolean;
        scheduleReminder: boolean;
      };
    }
  | {
      type: 'ISSUE_INVOICE_QBO_SYNC';
      invoiceId: string;
      quoteId: string;
    }
  | {
      type: 'RETRY_QBO_SYNC';
      invoiceId: string;
      quoteId: string;
    }
  | {
      type: 'REFRESH_BALANCE';
      invoiceId: string;
      quoteId: string;
      qboInvoiceId?: string;
    };

export interface InvoiceManagerResumePayloadV1 {
  version: 1;
  createdAt: number;
  provider: OAuthProvider;
  quoteId: string;
  invoiceId: string;
  step: 'BUILD' | 'PREVIEW' | 'SEND' | 'CONFIRM';
  previewPhase?: 'DRAFT' | 'ISSUED';
  pendingAction: InvoiceManagerPendingAction;
}

const STORAGE_KEY = 'invoice_manager_resume_v1';

export function saveInvoiceManagerResumePayload(payload: InvoiceManagerResumePayloadV1): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
}

export function clearInvoiceManagerResumePayload(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

export function readInvoiceManagerResumePayload(): InvoiceManagerResumePayloadV1 | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<InvoiceManagerResumePayloadV1>;
    if (parsed.version !== 1) return null;
    if (typeof parsed.createdAt !== 'number') return null;
    if (parsed.provider !== 'qbo' && parsed.provider !== 'm365') return null;
    if (!parsed.quoteId || typeof parsed.quoteId !== 'string') return null;
    if (!parsed.invoiceId || typeof parsed.invoiceId !== 'string') return null;
    if (!parsed.step || typeof parsed.step !== 'string') return null;
    if (!parsed.pendingAction || typeof parsed.pendingAction !== 'object') return null;
    return parsed as InvoiceManagerResumePayloadV1;
  } catch {
    return null;
  }
}

export function consumeInvoiceManagerResumePayload(): InvoiceManagerResumePayloadV1 | null {
  const payload = readInvoiceManagerResumePayload();
  if (payload) {
    clearInvoiceManagerResumePayload();
  }
  return payload;
}
